// apps/client/src/hooks/useMonthlyTableBreakdown.ts
// Phase 2: Fetches office/template breakdown data for drill-down table
// Calls getMonthlyTableData with includeBreakdown: true
// MIGRATED (2026-07-10): Fetches through the shared dashboard query cache (useDashboardQuery).

import { useMemo } from 'react';
import { useDashboardQuery } from './useDashboardQuery';

export interface OfficeTemplateBreakdown {
  month: string;
  officeId: string;
  templateId: string;
  entityCode?: string;
  upfrontCents: number;
  trailCents: number;
  // No settlement fields: this breakdown is keyed by commission run month, and a
  // settlement belongs to the month the loan settled (settlement figures come
  // from the settlement-date query on the parent month row only).
  lineOfTrail: number;
  clawbackCents: number;
}

export interface LotDimensionSnapshot {
  month: string;
  dimensionType: string;
  dimensionId: string;
  loanBookCents: number;
  lineOfTrail: number;
}

interface MonthlyTableDataResponse {
  months: any[];
  totals: any;
  metadata: any;
  byOfficeTemplate?: Array<{
    month: string;
    officeId: string;
    templateId: string;
    entityCode?: string;
    upfrontCents: number;
    trailCents: number;
    lineOfTrail: number;
    clawbackCents: number;
  }>;
  lotByDimension?: LotDimensionSnapshot[];
}

interface UseMonthlyTableBreakdownParams {
  companyId: string;
  startMonth: string;
  endMonth: string;
  enabled: boolean; // Only fetch when table view is active
  entityCode?: string;
}

/**
 * Hook for fetching office/template breakdown data for drill-down table.
 * Calls getMonthlyTableData with includeBreakdown: true.
 * Only fetches when enabled (table view is active) to avoid unnecessary queries.
 */
export function useMonthlyTableBreakdown({
  companyId,
  startMonth,
  endMonth,
  enabled,
  entityCode,
}: UseMonthlyTableBreakdownParams) {
  // Only fetch when the table view is active and a month range is set; otherwise
  // the query stays disabled and the empty breakdown below is returned.
  const isEnabled = enabled && !!startMonth && !!endMonth;

  const { data, loading, error } = useDashboardQuery<MonthlyTableDataResponse>(
    'getMonthlyTableData',
    {
      companyId,
      startMonth,
      endMonth,
      includeBreakdown: true,
      entityCode,
    },
    { enabled: isEnabled }
  );

  // When disabled, clear to the empty state regardless of any cached data.
  const byOfficeTemplate = useMemo<OfficeTemplateBreakdown[]>(
    () => (isEnabled ? (data?.byOfficeTemplate ?? []) : []),
    [isEnabled, data]
  );
  const lotByDimension = useMemo<LotDimensionSnapshot[]>(
    () => (isEnabled ? (data?.lotByDimension ?? []) : []),
    [isEnabled, data]
  );

  return { byOfficeTemplate, lotByDimension, loading, error };
}
